/**
 * Serial Manager - Web Serial API Integration
 * Handles USB Serial connection to Arduino/ESP32
 */

export class SerialManager {
    constructor() {
        this.port = null;
        this.reader = null;
        this.writer = null;
        this.isConnected = false;
        this.isReading = false;

        // Callbacks
        this.onDataCallback = null;
        this.onStatusChangeCallback = null;

        // Buffer for incomplete data
        this.dataBuffer = '';

        // Connection settings
        this.baudRate = 115200;
    }

    /**
     * Check if Web Serial API is supported
     */
    isSupported() {
        return 'serial' in navigator;
    }

    /**
     * Request serial port from user
     */
    async requestPort() {
        try {
            if (!this.isSupported()) {
                throw new Error('Web Serial API not supported. Please use Chrome or Edge.');
            }

            console.log('🔌 Requesting serial port...');

            // Request port with filters for common Arduino/ESP32 USB IDs
            this.port = await navigator.serial.requestPort({
                filters: [
                    // ESP32 common chip
                    { usbVendorId: 0x10C4 }, // CP2102
                    { usbVendorId: 0x1A86 }, // CH340
                    // Arduino
                    { usbVendorId: 0x2341 }, // Arduino
                    { usbVendorId: 0x2A03 }, // Arduino
                    // FTDI
                    { usbVendorId: 0x0403 }  // FTDI
                ]
            });

            console.log('✅ Serial port selected:', this.port.getInfo());
            return this.port;

        } catch (error) {
            console.error('❌ Serial port selection error:', error);
            throw error;
        }
    }

    /**
     * Connect to serial port
     */
    async connect(baudRate = 115200) {
        try {
            if (!this.port) {
                await this.requestPort();
            }

            this.baudRate = baudRate;

            console.log(`🔌 Opening serial port at ${this.baudRate} baud...`);

            // Open port
            await this.port.open({
                baudRate: this.baudRate,
                dataBits: 8,
                stopBits: 1,
                parity: 'none',
                flowControl: 'none'
            });

            this.isConnected = true;

            // Get writer for sending data
            this.writer = this.port.writable.getWriter();

            console.log('✅ Serial port connected!');

            if (this.onStatusChangeCallback) {
                this.onStatusChangeCallback('connected');
            }

            // Start reading data
            this.startReading();

            return true;

        } catch (error) {
            console.error('❌ Serial connection error:', error);
            this.isConnected = false;

            if (this.onStatusChangeCallback) {
                this.onStatusChangeCallback('disconnected');
            }

            throw error;
        }
    }

    /**
     * Start reading data from serial port
     */
    async startReading() {
        if (this.isReading) return;

        this.isReading = true;
        const decoder = new TextDecoderStream();
        const readableStreamClosed = this.port.readable.pipeTo(decoder.writable);
        this.reader = decoder.readable.getReader();

        console.log('📖 Started reading serial data...');

        try {
            while (this.isReading) {
                const { value, done } = await this.reader.read();

                if (done) {
                    console.log('📖 Reader closed');
                    break;
                }

                if (value) {
                    this.processData(value);
                }
            }
        } catch (error) {
            console.error('❌ Serial read error:', error);
        } finally {
            this.reader.releaseLock();
            await readableStreamClosed.catch(() => { });
        }
    }

    /**
     * Process incoming serial data
     */
    processData(data) {
        // Add to buffer
        this.dataBuffer += data;

        // Split by newline
        const lines = this.dataBuffer.split('\n');

        // Keep last incomplete line in buffer
        this.dataBuffer = lines.pop() || '';

        // Process complete lines
        for (let line of lines) {
            line = line.trim();
            if (line && this.onDataCallback) {
                this.onDataCallback(line);
            }
        }
    }

    /**
     * Send command to serial device
     */
    async sendCommand(command) {
        if (!this.isConnected || !this.writer) {
            throw new Error('Not connected to serial port');
        }

        try {
            const encoder = new TextEncoder();
            const data = encoder.encode(command + '\n');
            await this.writer.write(data);
            console.log('📤 Sent command:', command);
            return true;
        } catch (error) {
            console.error('❌ Send command error:', error);
            throw error;
        }
    }

    /**
     * Disconnect from serial port
     */
    async disconnect() {
        try {
            this.isReading = false;

            if (this.reader) {
                await this.reader.cancel();
                this.reader = null;
            }

            if (this.writer) {
                this.writer.releaseLock();
                this.writer = null;
            }

            if (this.port) {
                await this.port.close();
                this.port = null;
            }

            this.isConnected = false;

            console.log('🔌 Serial port disconnected');

            if (this.onStatusChangeCallback) {
                this.onStatusChangeCallback('disconnected');
            }

        } catch (error) {
            console.error('❌ Disconnect error:', error);
        }
    }

    /**
     * Set callback for received data
     */
    onData(callback) {
        this.onDataCallback = callback;
    }

    /**
     * Set callback for status changes
     */
    onStatusChange(callback) {
        this.onStatusChangeCallback = callback;
    }

    /**
     * Get connection status
     */
    getStatus() {
        return this.isConnected ? 'connected' : 'disconnected';
    }

    /**
     * Get port info
     */
    getPortInfo() {
        if (!this.port) return null;
        return this.port.getInfo();
    }
}
